/**
 * ACTIVITY-CORE (L2) — Trash: soft-deleted lists and tasks, with restore
 * and permanent delete. Local-first: operates over active account SQLite
 * write-path (the server records restored/deleted).
 */
import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { ArchiveRestore, ListChecks, ListTodo, type LucideIcon, Trash2 } from "@fleet/icons";
import {
  getTrashedListsQuery,
  getTrashedTodosQuery,
  purgeListMutation,
  purgeTodoMutation,
  queryKeys,
  restoreListMutation,
  restoreTodoMutation,
} from "@cartero/shared";
import { createPowerSyncQuery } from "@cartero/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@fleet/ui";
import { formatRelativeTime } from "@/features/activity/lib/activity-text";

interface TrashRowProps {
  icon: LucideIcon;
  name: string;
  deletedAt: string | null;
  onRestore: () => void;
  onPurge: () => void;
  testId: string;
}

function TrashRow(props: TrashRowProps) {
  const { t, locale } = useI18n();
  const [confirmOpen, setConfirmOpen] = createSignal(false);

  return (
    <div
      class="flex items-center gap-3 rounded-xl border border-border/30 bg-card/60 px-4 py-3"
      data-testid={props.testId}
    >
      <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        {(() => {
          const IconComp = props.icon;
          return <IconComp class="h-4.5 w-4.5" />;
        })()}
      </div>
      <div class="min-w-0 flex-1">
        <p class="truncate text-sm font-medium">{props.name}</p>
        <Show when={props.deletedAt}>
          <p class="text-xs text-muted-foreground">
            {t(msg`Deleted {when}`, { when: formatRelativeTime(props.deletedAt, locale()) })}
          </p>
        </Show>
      </div>
      <button
        type="button"
        class="shrink-0 rounded-md border border-border/40 px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
        onClick={() => props.onRestore()}
        data-testid={`${props.testId}-restore`}
      >
        <span class="inline-flex items-center gap-1.5">
          <ArchiveRestore class="h-3.5 w-3.5" />
          {t(msg`Restore`)}
        </span>
      </button>
      <button
        type="button"
        class="shrink-0 rounded-md border border-destructive/30 px-2.5 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
        onClick={() => setConfirmOpen(true)}
        data-testid={`${props.testId}-purge`}
        title={t(msg`Delete forever`)}
      >
        <Trash2 class="h-3.5 w-3.5" />
      </button>
      <ConfirmDialog
        open={confirmOpen()}
        onOpenChange={setConfirmOpen}
        title={t(msg`Delete forever?`)}
        description={t(msg`"{name}" will be permanently deleted. This cannot be undone.`, {
          name: props.name,
        })}
        variant="destructive"
        confirmLabel={t(msg`Delete forever`)}
        onConfirm={props.onPurge}
      />
    </div>
  );
}

function TrashSkeleton() {
  return (
    <div class="space-y-3" data-testid="trash-skeleton">
      <For each={[0, 1, 2]}>
        {() => (
          <div class="flex items-center gap-3 rounded-xl border border-border/20 px-4 py-3">
            <Skeleton class="h-9 w-9 animate-shimmer rounded-lg" />
            <div class="flex-1 space-y-1.5">
              <Skeleton class="h-3.5 w-1/2 animate-shimmer rounded" />
              <Skeleton class="h-3 w-1/4 animate-shimmer rounded" />
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

export function TrashView() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  const trashedLists = createPowerSyncQuery({
    queryKey: () => queryKeys.trash.lists(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = userId();
      return dbInstance && uid ? getTrashedListsQuery(dbInstance, uid) : null;
    },
    enabled: () => Boolean(drizzle() && userId()),
  });

  const trashedTodos = createPowerSyncQuery({
    queryKey: () => queryKeys.trash.todos(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = userId();
      return dbInstance && uid ? getTrashedTodosQuery(dbInstance, uid) : null;
    },
    enabled: () => Boolean(drizzle() && userId()),
  });

  const isLoading = () =>
    (trashedLists.isFetching || trashedTodos.isFetching) &&
    (trashedLists.data ?? []).length === 0 &&
    (trashedTodos.data ?? []).length === 0;

  const isEmpty = () =>
    (trashedLists.data ?? []).length === 0 && (trashedTodos.data ?? []).length === 0;

  const run = async (fn: () => Promise<void>, successMessage: string) => {
    try {
      await fn();
      showToast(successMessage, "success");
    } catch (err: unknown) {
      console.error("[trash] operation failed:", err);
      showToast(t(msg`Something went wrong`), "error");
    }
  };

  return (
    <div class="space-y-6" data-testid="trash-page">
      <div class="flex items-center gap-3">
        <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Trash2 class="h-5 w-5" />
        </div>
        <div>
          <h1 class="text-2xl font-bold tracking-tight">{t(msg`Trash`)}</h1>
          <p class="text-sm text-muted-foreground">
            {t(msg`Deleted lists and tasks. Restore them or delete them forever.`)}
          </p>
        </div>
      </div>

      <Show when={!isLoading()} fallback={<TrashSkeleton />}>
        <Show
          when={!isEmpty()}
          fallback={
            <EmptyState
              icon={Trash2}
              title={t(msg`Trash is empty`)}
              description={t(msg`Deleted lists and tasks will wait here until you restore them.`)}
            />
          }
        >
          <div class="space-y-6">
            <Show when={(trashedLists.data ?? []).length > 0}>
              <section class="space-y-2">
                <h2 class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(msg`Lists`)}
                </h2>
                <For each={trashedLists.data ?? []}>
                  {(row) => (
                    <TrashRow
                      icon={ListChecks}
                      name={row.name ?? t(msg`Untitled list`)}
                      deletedAt={row.deleted_at}
                      testId="trash-list"
                      onRestore={() => {
                        const dbInstance = drizzle();
                        if (!dbInstance) {
                          return;
                        }
                        void run(
                          () => restoreListMutation(dbInstance, row.id),
                          t(msg`List restored`),
                        );
                      }}
                      onPurge={() => {
                        const dbInstance = drizzle();
                        if (!dbInstance) {
                          return;
                        }
                        void run(
                          () => purgeListMutation(dbInstance, row.id),
                          t(msg`List permanently deleted`),
                        );
                      }}
                    />
                  )}
                </For>
              </section>
            </Show>

            <Show when={(trashedTodos.data ?? []).length > 0}>
              <section class="space-y-2">
                <h2 class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(msg`Tasks`)}
                </h2>
                <For each={trashedTodos.data ?? []}>
                  {(row) => (
                    <TrashRow
                      icon={ListTodo}
                      name={row.text ?? t(msg`Untitled task`)}
                      deletedAt={row.deleted_at}
                      testId="trash-todo"
                      onRestore={() => {
                        const dbInstance = drizzle();
                        if (!dbInstance) {
                          return;
                        }
                        void run(
                          () => restoreTodoMutation(dbInstance, row.id),
                          t(msg`Task restored`),
                        );
                      }}
                      onPurge={() => {
                        const dbInstance = drizzle();
                        if (!dbInstance) {
                          return;
                        }
                        void run(
                          () => purgeTodoMutation(dbInstance, row.id),
                          t(msg`Task permanently deleted`),
                        );
                      }}
                    />
                  )}
                </For>
              </section>
            </Show>
          </div>
        </Show>
      </Show>
    </div>
  );
}
